/**
 * A right-hand sheet to browse past conversations and load one into the simulator: the list
 * on the left, the selected one's transcript on the right. Loading hands the messages and the
 * conversation back to the simulator, which puts them in the editable thread and keeps the
 * real conversation id. Browsing runs no AI.
 */

import { useEffect, useMemo, useState, type ReactNode, type UIEvent } from 'react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { MessagesSquare, ListFilter, Loader2, Inbox, Search } from 'lucide-react';
import { isAfter, startOfDay } from 'date-fns';
import { useAllConversations, useConversationMessages } from '@/hooks/use-messages';
import { messageService } from '@/services/message.service';
import type { ConversationFilters } from '@/modules/shadow/components/conversation-filters';
import type { ConversationSummary } from '@/services/message.service';
import type { Message } from '@/types/shadow.types';
import { FieldInput } from '../ui/field';
import { PanelState } from '../ui/panel-state';
import { PillButton } from '../ui/pill-button';
import { humanize } from '../ui/text';
import { ConvItem, MsgBubble } from './conversation-parts';
import { contactLabel } from './conversation-format';

const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type='button'
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'cursor-pointer rounded-full border px-2.5 text-xs leading-6 transition-colors',
      active ? 'border-ink bg-ink text-white' : 'border-gray-500 bg-white text-text-body hover:bg-gray-100'
    )}
  >
    {children}
  </button>
);

// ─── Picker body (mounted only while the sheet is open) ─────────────────────

interface PickerBodyProps {
  onLoad: (conversation: ConversationSummary, messages: Message[]) => void;
}

const PickerBody = ({ onLoad }: PickerBodyProps) => {
  const [selected, setSelected] = useState<ConversationSummary | null>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);
  const [intentOptions, setIntentOptions] = useState<string[]>([]);
  const [selectedIntents, setSelectedIntents] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate?: Date; endDate?: Date }>({});
  useEffect(() => {
    let cancelled = false;
    messageService
      .getConversationIntents()
      .then((intents) => {
        if (!cancelled) setIntentOptions(intents);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const toggleIntent = (intent: string) =>
    setSelectedIntents((prev) => (prev.includes(intent) ? prev.filter((i) => i !== intent) : [...prev, intent]));

  // One smart box: a bare number (or #number) is an exact ticket-id lookup,
  // anything else searches the contact's name/email. Intent chips AND with it.
  const filters = useMemo<ConversationFilters | undefined>(() => {
    const base: ConversationFilters = {};
    if (debouncedSearch) {
      if (/^#?\d+$/.test(debouncedSearch)) base.conversationIdSearch = debouncedSearch.replace(/^#/, '');
      else base.contactSearch = debouncedSearch;
    }
    if (selectedIntents.length > 0) base.intents = selectedIntents;
    if (dateRange.startDate) base.startDate = dateRange.startDate;
    if (dateRange.endDate) base.endDate = dateRange.endDate;
    return Object.keys(base).length ? base : undefined;
  }, [debouncedSearch, selectedIntents, dateRange]);
  const activeFilterCount = selectedIntents.length + (dateRange.startDate || dateRange.endDate ? 1 : 0);
  const clearFilters = () => {
    setSelectedIntents([]);
    setDateRange({});
  };
  const { data: conversations, isLoading, hasMore, fetchNextPage } = useAllConversations(filters);
  const {
    data: messages,
    isLoading: msgsLoading,
    hasMore: msgsHasMore,
    fetchNextPage: fetchMoreMessages,
    isFetchingNextPage: fetchingMoreMessages,
  } = useConversationMessages(selected?.conversationId);

  const onListScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) fetchNextPage();
  };

  const loadable = messages.filter((m) => m.messageType !== 'activity' && m.content?.trim());

  return (
    <>
      <SheetHeader className='border-b border-gray-500 px-4 py-3'>
        <SheetTitle className='text-[15px] text-text-heading'>Pick a conversation</SheetTitle>
        <SheetDescription className='text-xs text-gray-800'>Load a real ticket into the simulator.</SheetDescription>
      </SheetHeader>

      <div className='flex min-h-0 flex-1'>
        <div className='flex w-[30%] min-w-[260px] shrink-0 flex-col border-r border-gray-500'>
          <div className='flex flex-col gap-3 border-b border-gray-500 p-3'>
            <div className='flex items-center gap-2'>
              <div className='relative min-w-0 flex-1'>
                <Search className='pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-gray-800' />
                <FieldInput
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder='Contact or #ticket id'
                  className='pl-8'
                />
              </div>
              <PillButton onClick={() => setFiltersOpen((o) => !o)} className={cn('h-8 shrink-0', filtersOpen && 'bg-gray-100')}>
                <ListFilter className='h-3.5 w-3.5' />
                Filters
                {activeFilterCount > 0 && (
                  <span className='rounded-full bg-ink px-1.5 text-[10px] leading-4 font-medium text-white'>
                    {activeFilterCount}
                  </span>
                )}
              </PillButton>
            </div>
            {filtersOpen && (
              <div className='flex flex-col gap-3'>
                {intentOptions.length > 0 && (
                  <div className='flex flex-col gap-1.5'>
                    <span className='text-xs leading-4 text-gray-800'>Intent</span>
                    <div className='flex max-h-24 flex-wrap gap-1.5 overflow-y-auto'>
                      {intentOptions.map((intent) => (
                        <Chip key={intent} active={selectedIntents.includes(intent)} onClick={() => toggleIntent(intent)}>
                          {humanize(intent)}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
                <div className='flex flex-col gap-1.5'>
                  <span className='text-xs leading-4 text-gray-800'>Date range</span>
                  <DateRangePicker
                    inline
                    value={dateRange}
                    onChange={setDateRange}
                    disabled={(date) => isAfter(startOfDay(date), startOfDay(new Date()))}
                    placeholder={{ start: 'From', end: 'To' }}
                  />
                </div>
                {activeFilterCount > 0 && (
                  <PillButton variant='ghost' className='w-fit self-end' onClick={clearFilters}>
                    Clear filters
                  </PillButton>
                )}
              </div>
            )}
          </div>
          <div onScroll={onListScroll} className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
            {isLoading ? (
              <PanelState icon={<Loader2 className='h-4 w-4 animate-spin' />}>Loading conversations</PanelState>
            ) : conversations.length === 0 ? (
              <PanelState icon={<Inbox className='h-5 w-5' />}>No conversations found.</PanelState>
            ) : (
              <>
                {conversations.map((c) => (
                  <ConvItem
                    key={c.conversationId}
                    conv={c}
                    selected={selected?.conversationId === c.conversationId}
                    onSelect={() => setSelected(c)}
                  />
                ))}
                {hasMore && (
                  <div className='flex justify-center py-3'>
                    <Loader2 className='h-4 w-4 animate-spin text-gray-800' />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className='flex min-h-0 flex-1 flex-col'>
          {!selected ? (
            <PanelState icon={<MessagesSquare className='h-5 w-5' />}>Select a conversation to preview it.</PanelState>
          ) : msgsLoading ? (
            <PanelState icon={<Loader2 className='h-4 w-4 animate-spin' />}>Loading messages</PanelState>
          ) : (
            <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-6'>
              {loadable.map((m, i) => (
                <MsgBubble key={`${m.id}-${i}`} m={m} />
              ))}
              {msgsHasMore && (
                <PillButton
                  variant='ghost'
                  className='mx-auto'
                  onClick={fetchMoreMessages}
                  disabled={fetchingMoreMessages}
                >
                  {fetchingMoreMessages ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : 'Load more messages'}
                </PillButton>
              )}
            </div>
          )}
        </div>
      </div>

      <SheetFooter className='flex-row items-center justify-between border-t border-gray-500 px-4 py-3'>
        <span className='truncate text-xs text-gray-800'>
          {selected
            ? `#${selected.conversationId} · ${contactLabel(selected)} · ${loadable.length} message${loadable.length === 1 ? '' : 's'}`
            : 'No conversation selected'}
        </span>
        <PillButton
          variant='primary'
          className='h-8 px-4'
          disabled={!selected || msgsLoading || loadable.length === 0}
          onClick={() => selected && onLoad(selected, loadable)}
        >
          Load conversation
        </PillButton>
      </SheetFooter>
    </>
  );
};

// ─── Sheet shell ────────────────────────────────────────────────────────────

interface ConversationPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoad: (conversation: ConversationSummary, messages: Message[]) => void;
}

export const ConversationPicker = ({ open, onOpenChange, onLoad }: ConversationPickerProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    {/* The body's data hooks mount only while the sheet is open. */}
    <SheetContent side='right' className='flex w-[70vw] flex-col gap-0 bg-background p-0 backdrop-blur-none sm:max-w-none'>
      <PickerBody onLoad={onLoad} />
    </SheetContent>
  </Sheet>
);
